import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Flame, Snowflake, Activity } from "lucide-react";

// Color stops (bottom -> top): green (cool) -> cyan -> yellow -> red (hot)
function colorAt(percent) {
    const p = Math.max(0, Math.min(100, percent));
    // 0-33: green->cyan, 33-66: cyan->yellow, 66-100: yellow->red
    if (p < 33) return interp("#22c55e", "#22d3ee", p / 33);
    if (p < 66) return interp("#22d3ee", "#facc15", (p - 33) / 33);
    return interp("#facc15", "#ef4444", (p - 66) / 34);
}

function interp(c1, c2, t) {
    const a = parseInt(c1.slice(1), 16);
    const b = parseInt(c2.slice(1), 16);
    const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
    const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
    const r = Math.round(ar + (br - ar) * t);
    const g = Math.round(ag + (bg - ag) * t);
    const bl = Math.round(ab + (bb - ab) * t);
    return `rgb(${r}, ${g}, ${bl})`;
}

function zoneFor(p) {
    if (p >= 90) return { label: "INFERNO", icon: Flame };
    if (p >= 75) return { label: "BLAZING", icon: Flame };
    if (p >= 55) return { label: "WARM", icon: Activity };
    if (p >= 30) return { label: "COOL", icon: Activity };
    return { label: "FROZEN", icon: Snowflake };
}

export const FlowMeter = ({ compact = false }) => {
    const [data, setData] = useState({ winrate: 50, label: null });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const res = await api.get("/flow-meter");
                if (alive) setData(res.data || { winrate: 50 });
            } catch (e) {}
            finally { if (alive) setLoading(false); }
        };
        load();
        const id = setInterval(load, 8000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const winrate = data.winrate ?? 50;
    const color = colorAt(winrate);
    const zone = zoneFor(winrate);
    const Icon = zone.icon;

    // Half-circle gauge using stroke-dashoffset
    const RADIUS = 80;
    const CIRC = Math.PI * RADIUS; // half-circle circumference
    const dashoffset = CIRC - (CIRC * winrate) / 100;

    return (
        <section className={`relative pp-bg-0 ${compact ? "py-6" : "py-12"}`} data-testid="flow-meter-section">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="rounded-2xl pp-bg-1 border border-[#242436] overflow-hidden" style={{ boxShadow: `0 0 0 1px ${color}33, 0 0 36px ${color}28` }}>
                    <div className="p-6 sm:p-8">
                        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                            <div>
                                <p className="text-xs font-mono uppercase tracking-widest" style={{ color }}>Today’s Flow Meter</p>
                                <h3 className="font-display text-3xl sm:text-4xl tracking-widest text-white">FLOW</h3>
                                <p className="mt-1 text-sm text-[#b7b7c6]">{data.label || "How hot we’re running today."}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-mono uppercase text-xs tracking-widest" style={{ background: `${color}18`, color, border: `1px solid ${color}55` }}>
                                    <Icon className="w-3.5 h-3.5" /> {zone.label}
                                </span>
                                <span className="font-display text-5xl sm:text-6xl tracking-widest" style={{ color, textShadow: `0 0 22px ${color}88` }} data-testid="flow-meter-percent">
                                    {winrate}<span className="text-2xl align-top">%</span>
                                </span>
                            </div>
                        </div>

                        <div className="relative w-full" style={{ height: 110 }}>
                            <svg viewBox="0 0 200 110" className="w-full h-full" preserveAspectRatio="xMidYMax meet">
                                <defs>
                                    <linearGradient id="flow-grad" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#22c55e" />
                                        <stop offset="33%" stopColor="#22d3ee" />
                                        <stop offset="66%" stopColor="#facc15" />
                                        <stop offset="100%" stopColor="#ef4444" />
                                    </linearGradient>
                                    <filter id="flow-glow" x="-50%" y="-50%" width="200%" height="200%">
                                        <feGaussianBlur stdDeviation="3" result="b" />
                                        <feMerge>
                                            <feMergeNode in="b" />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                </defs>
                                {/* Background arc */}
                                <path d="M20 100 A80 80 0 0 1 180 100" stroke="#242436" strokeWidth="14" fill="none" strokeLinecap="round" />
                                {/* Foreground gradient arc up to winrate */}
                                <motion.path
                                    d="M20 100 A80 80 0 0 1 180 100"
                                    stroke="url(#flow-grad)"
                                    strokeWidth="14"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeDasharray={CIRC}
                                    initial={{ strokeDashoffset: CIRC }}
                                    animate={{ strokeDashoffset: dashoffset }}
                                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                                    filter="url(#flow-glow)"
                                />
                                {/* Tick marks */}
                                {[0, 25, 50, 75, 100].map((t) => {
                                    const a = Math.PI * (1 - t / 100);
                                    const x = 100 + 88 * Math.cos(a);
                                    const y = 100 - 88 * Math.sin(a);
                                    return (
                                        <g key={t}>
                                            <circle cx={x} cy={y} r="1.6" fill={colorAt(t)} />
                                        </g>
                                    );
                                })}
                                {/* Needle */}
                                <motion.g
                                    animate={{ rotate: -90 + (winrate / 100) * 180 }}
                                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                                    style={{ transformOrigin: "100px 100px" }}
                                >
                                    <line x1="100" y1="100" x2="100" y2="32" stroke={color} strokeWidth="3" strokeLinecap="round" />
                                    <circle cx="100" cy="32" r="4" fill={color} stroke="#0a0a0f" strokeWidth="1" />
                                </motion.g>
                                <circle cx="100" cy="100" r="6" fill="#0a0a0f" stroke={color} strokeWidth="2" />
                            </svg>
                        </div>

                        <div className="mt-1 flex justify-between text-[10px] font-mono uppercase text-[#8b8ba0]">
                            <span>0% · cool</span>
                            <span>50% · warm</span>
                            <span>100% · 🔥 inferno</span>
                        </div>
                        {data.updated_at && (
                            <p className="mt-2 text-[11px] font-mono text-[#5e5e72] text-right">Updated {new Date(data.updated_at).toLocaleTimeString()}</p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
